import Flex from '../ui/Flex';
import ProxyIcon from '@/assets/icons/ProxyIcon';
import FlagIcon from '@/assets/icons/FlagIcon';
import { useNavigate } from 'react-router';
import { proxyPoolRotationShortLabelMap } from '@fd/common/biz/proxyPoolRotationOptions';
import { ChevronRight, Trash2Icon } from 'lucide-react';
import { httpApi } from '@fd/common/api/basicHttpApi';
import { WithSkeleton } from '../ui/Skeleton';
import usePools from '@/hooks/queries/usePools';
import { ProxyTypeMetas } from '@fd/common/biz/proxyType';
import { useMemo, useTransition } from 'react';
import usePoolTrafficUsage from '@fd/frontend/hooks/queries/usePoolTrafficUsage';
import { PoolType } from '@fd/common/biz/PoolType';

const paddingX = 'px-4 first:pl-5 last:pr-5';
const thClassname = paddingX;
const tdClassname = `${paddingX} py-5 bg-stone-50 first:rounded-l-xl last:rounded-r-xl group-hover:bg-stone-100 transition-all `;
const tdMobileHidden = `${tdClassname} hidden sm:table-cell `;

export default function ProxyPoolTable() {
  const {
    data: pools,
    mutate: refreshPools,
    isLoading,
    isValidating,
  } = usePools();
  const [isDeletingPool, startDeletingPool] = useTransition();

  const handleDeleteProxyPool = async (e: React.MouseEvent, poolId: number) => {
    e.stopPropagation();
    startDeletingPool(async () => {
      await httpApi(`pools/pool/${poolId}`, undefined, 'DELETE');
      await refreshPools();
    });
  };

  const skeleton = (
    <>
      <div className="skeleton w-full h-5" />
      {Array.from({ length: pools ? pools.length : 3 }, (_, index) => (
        <div className="skeleton w-full h-14" key={index} />
      ))}
    </>
  );

  return (
    <WithSkeleton
      isLoading={isLoading || isValidating || isDeletingPool}
      skeleton={skeleton}
    >
      <table className="border-spacing-y-2 border-separate">
        <thead>
          <tr className="text-xs text-left font-semibold">
            <th className={`${thClassname} sm:w-max-96 sm:w-fit`}>Pool name</th>
            <th className={`${thClassname} hidden sm:table-cell`}>
              Proxy type
            </th>
            <th className={`${thClassname} hidden sm:table-cell`}>Location</th>
            <th className={`${thClassname} hidden sm:table-cell`}>Protocol</th>
            <th className={`${thClassname} hidden sm:table-cell`}>Rotation</th>
            <th className={`${thClassname} hidden sm:table-cell`}>
              Usage last 30 days
            </th>
            <th className={`${thClassname} hidden sm:table-cell `}></th>
          </tr>
        </thead>
        <tbody translate="no">
          {pools &&
            pools.map((pool) => (
              <PoolRow
                key={pool.id}
                pool={pool}
                handleDeleteProxyPool={handleDeleteProxyPool}
              />
            ))}
        </tbody>
      </table>
    </WithSkeleton>
  );
}

const PoolRow = ({
  pool,
  handleDeleteProxyPool,
}: {
  pool: PoolType;
  handleDeleteProxyPool: (e: React.MouseEvent, poolId: number) => void;
}) => {
  const { data: poolsUsageData } = usePoolTrafficUsage();

  const poolUsage = useMemo(() => {
    if (!poolsUsageData || !poolsUsageData?.[pool.id]) return '0.0GB';
    const usage = poolsUsageData[pool.id];
    if (usage < 0.1) return '<0.1GB';
    return `${usage.toFixed(1)}GB`;
  }, [poolsUsageData, pool.id]);

  const navigate = useNavigate();

  const handleRowClick = (poolId: number) => {
    if (window.getSelection()?.toString() === '')
      navigate(`/proxy-pool/${poolId}`);
  };

  return (
    <tr
      id={`proxy-pool-${pool.pool_name}`}
      className="text-sm font-normal group "
      key={pool.id}
      onClick={() => handleRowClick(pool.id)}
    >
      <td className={tdClassname}>{pool.pool_name}</td>
      <td className={tdMobileHidden}>
        {pool.proxy_type && (
          <Flex.Row className="gap-2">
            <ProxyIcon type={pool.proxy_type} className="stroke-gray-600" />
            <span className="text-gray-600">
              {ProxyTypeMetas[pool.proxy_type].title}
            </span>
          </Flex.Row>
        )}
      </td>
      <td className={tdMobileHidden}>
        <Flex.Row className="gap-2">
          {pool.countries && pool.countries.length > 0 && (
            <>
              <FlagIcon
                countryCode={pool.countries[0]}
                className="rounded-md"
              />
              <span className="text-gray-600">{pool.countries[0]}</span>
            </>
          )}
        </Flex.Row>
      </td>
      <td className={tdMobileHidden}>
        <span className="text-gray-600">
          {pool.protocol.length > 0 ? pool.protocol.toUpperCase() : 'HTTP'}
        </span>
      </td>
      <td className={tdMobileHidden}>
        <span className="text-gray-600">
          {proxyPoolRotationShortLabelMap.get(pool.rotation)}
        </span>
      </td>
      <td className={tdMobileHidden}>
        <Flex.Col className="gap-0">
          <span className="text-gray-600">{poolUsage}</span>
        </Flex.Col>
      </td>
      <td className={tdClassname}>
        <Flex.Row className="gap-2 items-center hover:cursor-pointer justify-end">
          <div
            className="pr-4 hidden sm:block"
            onClick={(e) => handleDeleteProxyPool(e, pool.id)}
          >
            <Trash2Icon className="stroke-gray-600 size-5 hover:stroke-red-400 transition-all text-center" />
          </div>
          <span className="text-gray-600">View proxies</span>
          <ChevronRight className="stroke-gray-600 group-hover:translate-x-2 transition-all" />
        </Flex.Row>
      </td>
    </tr>
  );
};
